import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIsMount } from '../../hooks/useIsMountHook';
import _, { isNil } from 'lodash';
import i18n from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { downloadAsset, fetchCertificatesThunk } from '../../store/certificates/certificatesActions';
import { CertificateType } from '../../store/certificates/certificatesTypes';
import { Certificate } from '../../types/rest/assetCatalogTypes';
import BzmIcon from '../icons/BzmIcon';
import CertificateDetailsModal, {
  AliasImport,
  convertAliasMetaToAliasImport,
} from '../../views/AssetCatalog/Certificates/CertificateDetailsModal';
import BlazeInputDropdown, { BlazeDropdownItem } from '../ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import clsx from 'clsx';
import styles from '../ui/servicePicker/ServicePicker.module.css';
import acApi from '../../axios/ac/acApi';
import { isKeystoreFile } from '../../utils/assetUtils';
import { Col, Form, Row } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import InputGroupContent from '../ui/InputGroupContent/InputGroupContent';
import BzFormControlWithHook from '../forms/BzFormControlWithHook';
import { useFormInput, ValidatorType } from '../../hooks/useFormInput';
import { REGEX_CONFIG_PROP } from '../../utils/regexUtils';

interface CertificateSelectorProps {
  /** On certificate selected event. */
  onSelectCertificate: (id: number, name?: string, alias?: string) => void;

  /** On certificate removed event */
  onRemoveCertificate?: (id: number) => void;

  /**
   * Change selected alias.
   * @param id certificate id
   * @param alias name of the alias
   */
  onSelectAlias?: (id: number, alias: string | null) => void;

  /** ID of the certificate which should be preselected. If value is "0" option "Select config value" gets selected*/
  selectedCertificateId?: number;

  selectedAlias?: string;

  /** For propagating upload validity to parent components. */
  setValidity?: (isValid: boolean) => void;

  disabled?: boolean;

  /** Keystore or client certificate */
  type: CertificateType;

  className?: string;

  /** Custom label for SSL options */
  label?: string;

  /** Optional variant of the styling.
   *
   * Standard: Dropdown with alias selector is rendered separately (default behaviour).
   * Inline: Both components are rendered in a single line.
   * */
  variant?: 'standard' | 'inline';

  /** If true, there will be an option in the list called "use configuration property" */
  allowConfigurationProperty?: boolean;

  /** When property value is updated */
  onUpdateConfigPropertyValue?: (configProperty: string) => void;

  /** Configuration property value*/
  configPropertyValue?: string;

  allowUnSelect?: boolean;
}

// Label dictionary for different type of asset files.
type CertificateSelectorDictionary = {
  inlineHelp: string;
  selectMessage: string;
  label: string;
  noItems: string;
  unSelectedMessage?: string;
};
const dict: {
  CERTIFICATE: CertificateSelectorDictionary;
  KEYSTORE: CertificateSelectorDictionary;
  ALL: CertificateSelectorDictionary;
} = {
  CERTIFICATE: {
    label: i18n.t('certificates:uploadComponent.certificates.label'),
    noItems: i18n.t('certificates:selectCertWidget.noCertsAvailable'),
    inlineHelp: i18n.t('certificates:uploadComponent.certificates.desc'),
    selectMessage: i18n.t('certificates:uploadComponent.certificates.select'),
    unSelectedMessage: i18n.t('certificates:uploadComponent.certificates.unSelect'),
  },
  KEYSTORE: {
    label: i18n.t('certificates:uploadComponent.keystore.label'),
    noItems: 'No Keystore files available in the workspace.',
    inlineHelp: i18n.t('certificates:uploadComponent.keystore.desc'),
    selectMessage: i18n.t('certificates:uploadComponent.keystore.select'),
    unSelectedMessage: i18n.t('certificates:uploadComponent.keystore.unSelect'),
  },
  ALL: {
    label: i18n.t('certificates:uploadComponent.keystore.label'),
    noItems: 'No Keystore files available in the workspace.',
    inlineHelp: i18n.t('certificates:uploadComponent.keystore.desc'),
    selectMessage: i18n.t('certificates:uploadComponent.keystore.select'),
    unSelectedMessage: i18n.t('certificates:uploadComponent.keystore.unSelect'),
  },
};

//This cert represents "Config option" in the dropdown
const certificateByConfigurationId = 0;
const certByConfig: Certificate = {
  id: certificateByConfigurationId,
  name: 'Use Configuration Property',
} as Certificate;

/**
 * Component which uploads certificate Asset and makes it usable.
 * @constructor
 */
const CertificateSelector = (props: CertificateSelectorProps) => {
  const [selectedCert, setSelectedCert] = useState<Certificate>(undefined);
  const isMount = useIsMount();
  const dispatch = useDispatch();
  const isLoading = useSelector((state: any) => state.certificates.isLoading);
  const certificates: Certificate[] = useSelector((state: any) => state.certificates.certificates);
  const [aliases, setAliases] = useState<AliasImport[] | null>(null);
  const [selectedAlias, setSelectedAlias] = useState<string | null>(props.selectedAlias ? props.selectedAlias : null);
  const [certificateDetail, setCertificateDetail] = useState<Certificate | null>(null);

  useEffect(() => {
    if (!_.isNil(props.selectedCertificateId) && !isMount) {
      getCertificates(true, true);
    }
  }, [props.selectedCertificateId]);

  useEffect(() => {
    //If selected cert is keystore file get list of aliases
    if (!isMount && selectedCert && isKeystoreFile(selectedCert.name)) {
      getAliasesForCertificate(selectedCert.primaryMetadata.aliases);
    }
  }, [selectedCert]);

  /**
   * Gets list of aliases for keystore file.
   * @param aliasList
   */
  const getAliasesForCertificate = (aliasList: any) => {
    const aliases: AliasImport[] = convertAliasMetaToAliasImport(aliasList).filter(a => a.type === 'KEY_PAIR');
    setAliases(aliases);
    // If there is no selected alias yet, and we have more than 1 alias available, preselect first
    if (_.isNil(selectedAlias) && _.size(aliases) > 0) {
      setTimeout(() => {
        // Timeout is set because there might be draft creation
        // in place in background resulting in draft not found
        let defaultSelectedAlias: AliasImport = undefined;
        for (let i = 0; i < aliases.length; i++) {
          const currentAlias = aliases[i];
          if (currentAlias.accessGranted === true) {
            defaultSelectedAlias = currentAlias;
            break;
          }
        }
        if (defaultSelectedAlias !== undefined) {
          aliasChangedHandler(defaultSelectedAlias.name);
        }
      }, 500);
    }
  };

  const refreshAliases = (cert: Certificate) => {
    // refresh only if we are editing the same certificate call useEffect and update Aliases
    if (selectedCert.id === cert.id) {
      setSelectedCert(cert);
    }
  };

  /**
   * Get cert list from backend.
   * @param silentReload - if true, we will not show any loading indicator
   * @param selectCert - if true, certificate injected via props will be selected after load.
   */
  const getCertificates = async (silentReload?: boolean, selectCert?: boolean) => {
    await dispatch(fetchCertificatesThunk(false, silentReload));
    if (!selectCert) {
      return;
    }

    // If selected id is 0, select the "Config" option
    if (props.selectedCertificateId === 0 && props.allowConfigurationProperty === true) {
      setSelectedCert(certByConfig);
      return;
    }

    // Select the cert from list of certificates
    const selectedCert = _.find(certificates, cert => cert.id === props.selectedCertificateId);
    if (!_.isNil(selectedCert)) {
      setSelectedCert(selectedCert);
    }
  };

  const prevCertificatesRef = useRef();
  useEffect(() => {
    const certificatesHaveChanged = !_.eq(certificates, prevCertificatesRef.current);
    if (isMount || (certificatesHaveChanged && !isLoading)) {
      getCertificates(false, true).finally(() => {
        prevCertificatesRef.current = certificates as any;
      });
    }
  }, [certificates]);

  /**
   * Resets selectors local state.
   */
  const resetState = () => {
    setSelectedAlias(null);
    setAliases(null);
  };

  const changeSelectedCertById = (item: BlazeDropdownItem) => {
    const certFound = filterCertificateList().filter(cert => cert.id === item.id);
    //-2 means go back to no selection (certificate of keystore)
    if (item.id === -2) {
      setSelectedCert(undefined);
      resetState();
      props.onSelectCertificate(item.id, item.name);
      return;
    }
    if (!isNil(certFound) && certFound.length > 0) {
      changeSelectedCert(certFound[0]);
      return;
    }
    if (!_.isNil(selectedCert)) {
      removeByFileName(selectedCert.name);
    }
  };

  const changeSelectedCert = (currentCert: Certificate) => {
    if (_.isNil(selectedCert) || currentCert.id !== selectedCert.id) {
      setSelectedCert(currentCert);
      resetState();
      if (currentCert.id !== certificateByConfigurationId) {
        props.onSelectCertificate(currentCert.id, currentCert.name);
      } else {
        //Reset value to empty
        configPropHook.resetValue('');
      }
    }
  };

  /**
   * Gets list of available certificates and creates a dropdown.
   */
  const filterCertificateList = (): Certificate[] => {
    const itemsList: Certificate[] = [];
    let filtered = certificates.filter(cert => {
      if (props.type === 'ALL') {
        return true;
      } else if (props.type === 'KEYSTORE' && isKeystoreFile(cert.name)) {
        return true;
      } else return props.type === 'CERTIFICATE' && !isKeystoreFile(cert.name);
    });
    filtered = filtered.slice().sort((c1, c2) => c1.name.localeCompare(c2.name));

    //If we should show "Config by property" value in the dropdown
    if (props.allowConfigurationProperty === true) {
      itemsList.push(certByConfig);
    }

    filtered.forEach(cert => itemsList.push(cert));
    return itemsList;
  };

  const showCertificateModal = () => {
    return (
      <CertificateDetailsModal
        onClose={() => {
          setCertificateDetail(null);
        }}
        fileName={certificateDetail.name}
        cert={certificateDetail}
        onValidateAliases={refreshAliases}
      />
    );
  };

  const getCert = async (certId: number) => {
    const response = (await acApi.assets.getById(certId)).data.result;
    setCertificateDetail(response as Certificate);
  };

  const getEditButtonDom = (cert: Certificate, isKeyStore: boolean) => {
    return (
      <span>
        <button
          id={`edit-${cert.id}`}
          className={clsx(
            styles.serviceItemIcons,
            'btn btn-link btn-xs btn-no-hover form-inline mr-2 serviceItemIcons',
          )}
          onClick={() => {
            getCert(cert.id);
          }}
        >
          <BzmIcon
            title={i18n.t('common:sslConfig.certificateContent.edit')}
            variant={isKeyStore === true ? 'icon-bzm-edit' : 'icon-bzm-show'}
            className={'icon-xs'}
          ></BzmIcon>
        </button>
      </span>
    );
  };
  const getDownloadButtonDom = (cert: Certificate) => {
    return (
      <span>
        <button
          id={`download-${cert.id}`}
          className={clsx(
            styles.serviceItemIcons,
            'btn btn-link btn-xs btn-no-hover form-inline mr-2 serviceItemIcons',
          )}
        >
          <BzmIcon
            title={i18n.t('common:sslConfig.certificateContent.download')}
            variant={'icon-bzm-download'}
            className={'icon-xs'}
            onClick={() => {
              dispatch(downloadAsset(cert));
            }}
          />
        </button>
      </span>
    );
  };

  const getCertificateItem = (cert: Certificate) => {
    let accessGranted = true;
    const isKeystore = props.type !== 'CERTIFICATE';
    if (isKeystore) {
      accessGranted =
        !_.isNil(cert.primaryMetadata?.keystore_access_granted) &&
        cert.primaryMetadata.keystore_access_granted === true;
    }
    const rowPointer = accessGranted ? undefined : 'none';
    const rowOpacity = accessGranted ? undefined : '0.5';
    return (
      <div
        style={{ minHeight: '28px', lineHeight: '38px' }}
        className={'d-flex'}
        onClick={event => {
          if (!accessGranted) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
      >
        <div style={{ width: '100%', pointerEvents: rowPointer, marginLeft: '16px' }} className="text-truncate">
          <span style={{ opacity: rowOpacity }} dangerouslySetInnerHTML={{ __html: cert.name }} />
        </div>
        <div className="ml-auto">
          <span
            className={clsx('hover-actions')}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {getEditButtonDom(cert, isKeystore)}
            {getDownloadButtonDom(cert)}
          </span>
        </div>
      </div>
    );
  };

  const getKeystoreCerts = (): BlazeDropdownItem[] => {
    const filtered = filterCertificateList();
    const hasSelectedCert =
      props.allowUnSelect && !_.isNil(selectedCert) && filtered.some(item => item.id === selectedCert.id);

    const noCertOption: BlazeDropdownItem[] = hasSelectedCert
      ? [{ id: -2, name: dict[props.type].unSelectedMessage }]
      : [];

    const certOptions = filtered.map(ct => ({
      id: ct.id,
      name: ct.name,
      htmlContent: ct.id === 0 ? undefined : getCertificateItem(ct),
    }));

    return [...noCertOption, ...certOptions];
  };

  const getSelectedKeystore = (): BlazeDropdownItem => {
    const keystoreCerts = getKeystoreCerts();
    if (!_.isNil(keystoreCerts) && keystoreCerts.length > 0) {
      if (!_.isNil(selectedCert)) {
        const keystoreCert: BlazeDropdownItem = keystoreCerts.find(item => item.id === selectedCert.id);
        return keystoreCert ? keystoreCert : defaultKeystore;
      } else {
        return defaultKeystore;
      }
    } else {
      return noKeystore;
    }
  };

  const getSelectTitle = (title: string) => {
    return <span className="text-muted font-italic" dangerouslySetInnerHTML={{ __html: title }} />;
  };

  const defaultAlias: BlazeDropdownItem = {
    id: '-2',
    name: 'Select as alias (Key Pair)...',
    htmlTitle: getSelectTitle('Select as alias (Key Pair)...'),
  };

  const noAliases: BlazeDropdownItem = {
    id: '-1',
    name: 'No alias found on Certificate',
    htmlTitle: getSelectTitle('No alias found on Certificate'),
  };

  const defaultKeystore: BlazeDropdownItem = {
    id: '-2',
    name: dict[props.type].selectMessage,
    htmlTitle: getSelectTitle(dict[props.type].selectMessage),
  };

  const noKeystore: BlazeDropdownItem = {
    id: '-1',
    name: dict[props.type].noItems,
    htmlTitle: getSelectTitle(dict[props.type].noItems),
  };

  const aliasChangedHandler = (alias: string | null) => {
    setSelectedAlias(alias);
    props.onSelectAlias(selectedCert.id, alias);
  };

  const getCertificateAliasesItem = (alias: AliasImport) => {
    const rowPointer = alias.accessGranted ? undefined : 'none';
    const rowOpacity = alias.accessGranted ? undefined : '0.5';
    return (
      <div
        style={{ minHeight: '28px', lineHeight: '38px' }}
        className={'d-flex'}
        onClick={event => {
          if (!alias.accessGranted) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
      >
        <div style={{ width: '100%', pointerEvents: rowPointer, marginLeft: '16px' }} className="text-truncate">
          <span style={{ opacity: rowOpacity }} dangerouslySetInnerHTML={{ __html: alias.name }} />
        </div>
        <div className="ml-auto">
          <span
            className={clsx('hover-actions')}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {alias.accessGranted === false && (
              <span className={clsx('hover-actions')}>
                <div
                  className={clsx(
                    styles.serviceItemIcons,
                    'btn btn-link btn-xs btn-no-hover form-inline mr-2 serviceItemIcons',
                  )}
                  title={i18n.t('common:sslConfig.aliases.wrongPassword')}
                >
                  <BzmIcon variant={'icon-bzm-danger'} className={'icon-xs text-danger'} />
                </div>
              </span>
            )}
          </span>
        </div>
      </div>
    );
  };

  const getSelectedCertificateAlias = (): BlazeDropdownItem => {
    const certAliases = getCertificateAliases();
    if (!_.isNil(certAliases) && certAliases.length > 0) {
      const alias = certAliases.find(ct => ct.name === selectedAlias);
      return !_.isNil(alias) ? alias : defaultAlias;
    } else {
      return noAliases;
    }
  };

  const getCertificateAliases = () => {
    if (_.isNil(aliases)) {
      return;
    }
    const aliasItems: BlazeDropdownItem[] = aliases.map((al, index) => ({
      id: String(index) as string,
      name: al.name,
      htmlContent: getCertificateAliasesItem(al),
    }));
    return aliasItems;
  };

  const removeByFileName = (fileName: string) => {
    if (props.onRemoveCertificate && selectedCert.name == fileName) {
      props.onRemoveCertificate(selectedCert.id);
    }
  };

  const isAliasDropdownVisible = () => {
    return !_.isNil(selectedCert) && !_.isNil(aliases);
  };

  /**
   * Gets dropdown with list of aliases.
   * @param showPrepend if true, the dropdown is wrapped with bootstrap prepend component.
   */
  const getAliasDropdown = (showPrepend: boolean) => {
    if (!isAliasDropdownVisible()) {
      return null;
    }

    const dropdown = (
      <BlazeInputDropdown
        id={'alias-picker'}
        showGroups={false}
        isSearchable={false}
        minDropdownMenuWidth={350}
        items={getCertificateAliases()}
        selected={getSelectedCertificateAlias()}
        onClick={(item: BlazeDropdownItem) => aliasChangedHandler(item.name)}
        isDefaultOptionSelectable={true}
        disabled={props.disabled || getSelectedKeystore().id === '-1'}
        padding={'0px'}
        dropDownItemClassName={'action-item'}
        dropdownWidth={'100%'}
      />
    );

    const dropdownWithPrepend = (
      <InputGroup>
        <InputGroup.Prepend>
          <span className="input-group-text">{i18n.t('common:sslConfig.alias.label')}</span>
        </InputGroup.Prepend>
        <InputGroupContent>{dropdown}</InputGroupContent>
      </InputGroup>
    );

    return (
      <>
        {!showPrepend && <label className={'mt-4'}>{i18n.t('common:sslConfig.alias.label')}</label>}
        {showPrepend ? dropdownWithPrepend : dropdown}
      </>
    );
  };

  // Use callback stays between renders
  // Make sure to put there dependency if we are calling a callback function !!!
  const updateCertConfigProperty = useCallback(
    _.debounce((value: string) => {
      if (!_.isNil(props.onUpdateConfigPropertyValue)) {
        props.onUpdateConfigPropertyValue(value);
      }
    }, 650),
    [props.onUpdateConfigPropertyValue],
  );

  const configPropHook = useFormInput(
    props.configPropertyValue,
    fn => {
      if (fn.isValid) {
        updateCertConfigProperty(fn.value as string);
      }
    },
    ...[
      {
        type: ValidatorType.REQUIRED,
        invalidMessage: i18n.t('common:sslConfig.config.notEmptyAllowed'),
      },
      {
        type: ValidatorType.INVALID_CHARACTERS,
        value: REGEX_CONFIG_PROP,
        invalidMessage: i18n.t('common:sslConfig.config.wrongFormat'),
      },
    ],
  );

  const certificateConfigField = (
    <Form.Group>
      <Form.Label>{i18n.t('common:sslConfig.config.label')}</Form.Label>
      <BzFormControlWithHook
        autoComplete={'off'}
        id={'confiPropId-' + _.uniqueId()}
        useFormInputHook={configPropHook}
        placeholder={i18n.t('common:sslConfig.config.placeholder')}
        required={true}
        hideErrors={false}
        disabled={props.disabled}
      />
    </Form.Group>
  );

  const getCertificatesDropdown = (showLabel: boolean) => {
    return (
      <>
        {showLabel && <label id={'certSelector' + _.uniqueId()}>{dict[props.type].label}</label>}
        <BlazeInputDropdown
          id={'cert-picker'}
          showGroups={false}
          isSearchable={false}
          minDropdownMenuWidth={350}
          items={getKeystoreCerts()}
          selected={getSelectedKeystore()}
          onClick={changeSelectedCertById}
          isDefaultOptionSelectable={true}
          disabled={props.disabled || getSelectedKeystore().id === '-1'}
          padding={'0px'}
          dropDownItemClassName={'action-item'}
        />
      </>
    );
  };

  return (
    <div className={props.className}>
      <>{!_.isNil(certificateDetail) && showCertificateModal()}</>
      {(props.variant === 'standard' || _.isNil(props.variant)) && (
        <>
          <div>{getCertificatesDropdown(true)}</div>
          {isAliasDropdownVisible() && <div>{getAliasDropdown(false)}</div>}
        </>
      )}

      {props.variant === 'inline' && (
        <Row>
          <Col md={isAliasDropdownVisible() ? 6 : 12}>{getCertificatesDropdown(false)}</Col>
          {isAliasDropdownVisible() && <Col md={6}>{getAliasDropdown(true)}</Col>}
        </Row>
      )}

      {selectedCert?.id === certificateByConfigurationId && <div className={'mt-4'}>{certificateConfigField}</div>}
    </div>
  );
};

export default CertificateSelector;
